import { Play, Heart, MessageCircle, Send, ChevronLeft, ChevronRight, User, Volume2, VolumeX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import VideoDetailModal from "./VideoDetailModal";

import { useTrackView } from "@/hooks/useTrackView";
import { sfxLike, sfxUnlike, sfxComment } from "@/lib/sfx";
import { triggerPointsAnimation } from "@/components/PointsRewardAnimation";
import { optimizeStorageUrl } from "@/lib/imageUtils";
import { useIsMobile } from "@/hooks/use-mobile";

type Post = {
  id: string;
  user_id: string;
  media_url: string;
  media_type: string;
  thumbnail_url: string;
  artist: string;
  venue: string;
  description: string;
  created_at: string;
  like_count: number;
  comment_count: number;
  liked_by_me: boolean;
  display_name?: string;
  avatar_url?: string;
  carousel_urls?: string[];
};

type Comment = {
  id: string;
  content: string;
  created_at: string;
  display_name: string;
};

const FALLBACK_POSTS = [
  { id: "f1", artist: "Taylor Swift", media_url: "https://videos.pexels.com/video-files/1763075/1763075-sd_640_360_30fps.mp4", media_type: "video", poster: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=400&h=400&fit=crop" },
  { id: "f2", artist: "Bad Bunny", media_url: "https://videos.pexels.com/video-files/1190735/1190735-sd_640_360_30fps.mp4", media_type: "video", poster: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop" },
  { id: "f3", artist: "The Weeknd", media_url: "https://videos.pexels.com/video-files/2022395/2022395-sd_640_360_30fps.mp4", media_type: "video", poster: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=400&fit=crop" },
  { id: "f4", artist: "Beyoncé", media_url: "https://videos.pexels.com/video-files/3141208/3141208-sd_640_360_30fps.mp4", media_type: "video", poster: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&h=400&fit=crop" },
  { id: "f5", artist: "Drake", media_url: "https://videos.pexels.com/video-files/1537189/1537189-sd_640_360_30fps.mp4", media_type: "video", poster: "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400&h=400&fit=crop" },
  { id: "f6", artist: "SZA", media_url: "https://videos.pexels.com/video-files/2795170/2795170-sd_640_360_25fps.mp4", media_type: "video", poster: "https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400&h=400&fit=crop" },
];


const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: (i: number) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  }),
};

const VideoCard = ({ post, index, onLike, onUnlike, liked, likeCount, commentCount, displayName, avatarUrl, userId, onOpenDetail, hoverSoundEnabled }: {
  post: { id: string; artist: string; media_url: string; media_type: string; poster?: string; description?: string; venue?: string; carousel_urls?: string[] };
  index: number;
  onLike: () => Promise<boolean>;
  onUnlike: () => void;
  liked: boolean;
  likeCount: number;
  commentCount: number;
  displayName?: string;
  avatarUrl?: string;
  userId?: string;
  onOpenDetail: () => void;
  hoverSoundEnabled: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [liveComments, setLiveComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLikeAnimating, setIsLikeAnimating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const trackView = useTrackView();
  const isMobile = useIsMobile();
  const [swipeHint, setSwipeHint] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      const video = videoRef.current;
      if (!video) return;
      if (entry.isIntersecting) {
        video.play().catch(() => {});
        setIsPlaying(true);
        trackView(post.id);
      }
      else { video.pause(); setIsPlaying(false); }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackView, post.id]);

  // Load comments when video starts playing
  const loadComments = useCallback(async () => {
    const { data } = await supabase
      .from("post_comments")
      .select("id, content, created_at, user_id")
      .eq("post_id", post.id)
      .order("created_at", { ascending: false })
      .limit(5);

    if (data) {
      const userIds = [...new Set(data.map(c => c.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds.length > 0 ? userIds : ["none"]);
      const nameMap = new Map(profiles?.map(p => [p.user_id, p.display_name]) ?? []);
      setLiveComments(data.reverse().map(c => ({
        id: c.id,
        content: c.content,
        created_at: c.created_at,
        display_name: nameMap.get(c.user_id) || "Fan",
      })));
    }
  }, [post.id]);

  useEffect(() => {
    if (isPlaying && !isFallback) loadComments();
  }, [isPlaying, loadComments]);

  const handleSendComment = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!newComment.trim()) return;
    const content = newComment.trim();
    await supabase.from("post_comments").insert({ post_id: post.id, user_id: user.id, content });
    sfxComment();
    triggerPointsAnimation(2);
    setNewComment("");
    // Add to live comments immediately
    setLiveComments(prev => [...prev.slice(-4), {
      id: Date.now().toString(),
      content,
      created_at: new Date().toISOString(),
      display_name: "You",
    }]);
  };

  const handleLikeClick = async (e: React.MouseEvent) => {
    if (!user) { navigate("/auth"); return; }
    setIsLikeAnimating(true);
    setTimeout(() => setIsLikeAnimating(false), 600);
    if (liked) { sfxUnlike(); onUnlike(); } else {
      sfxLike();
      const isFirstLike = await onLike();
      if (isFirstLike) {
        triggerPointsAnimation(1, e.clientX, e.clientY);
      }
    }
  };


  const isFallback = post.id.startsWith("f");
  const initials = (displayName || post.artist || "?").slice(0, 2).toUpperCase();

  return (
    <motion.div
      ref={cardRef}
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      className="group relative overflow-hidden rounded-2xl border border-border/50 bg-card shadow-card hover:shadow-elevated hover:border-primary/20 transition-all duration-500"
      drag={isMobile ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.3}
      onDragEnd={(_, info) => {
        if (info.offset.x < -80 && userId) {
          navigate(`/profile/${userId}`);
        }
      }}
      onDragStart={() => setSwipeHint(true)}
    >
      {/* Swipe-left profile hint */}
      {isMobile && swipeHint && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          className="absolute inset-y-0 right-0 w-16 bg-primary/20 backdrop-blur-sm flex items-center justify-center z-30 pointer-events-none rounded-r-2xl"
        >
          <User className="h-5 w-5 text-primary" />
        </motion.div>
      )}
      {/* Media with overlays */}
      <div
        className={`relative overflow-hidden cursor-pointer ${isMobile ? "aspect-[3/4] max-w-[75vw]" : "aspect-[3/4]"}`}
        onClick={onOpenDetail}
        onMouseEnter={() => {
          const video = videoRef.current;
          if (video && post.media_type === "video" && hoverSoundEnabled) {
            video.muted = false;
            video.volume = 0.5;
          }
        }}
        onMouseLeave={() => {
          const video = videoRef.current;
          if (video && post.media_type === "video") {
            video.muted = true;
          }
        }}
      >
        {post.poster && (
          <img src={post.poster} alt={post.artist} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${isPlaying ? "opacity-0" : "opacity-100"}`} />
        )}
        {post.media_type === "video" ? (
          <video ref={videoRef} src={post.media_url} poster={post.poster} muted loop playsInline preload="none" className="h-full w-full object-cover" />
        ) : post.media_type === "carousel" && post.carousel_urls && post.carousel_urls.length > 0 ? (
          <div className="relative h-full w-full">
            <AnimatePresence mode="wait">
              <motion.img
                key={carouselIndex}
                src={post.carousel_urls[carouselIndex]}
                alt={post.artist}
                className="h-full w-full object-cover absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </AnimatePresence>
            {post.carousel_urls.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setCarouselIndex((prev) => (prev - 1 + post.carousel_urls!.length) % post.carousel_urls!.length); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-background/60 backdrop-blur-sm flex items-center justify-center z-10"
                >
                  <ChevronLeft className="h-3.5 w-3.5 text-foreground" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setCarouselIndex((prev) => (prev + 1) % post.carousel_urls!.length); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-background/60 backdrop-blur-sm flex items-center justify-center z-10"
                >
                  <ChevronRight className="h-3.5 w-3.5 text-foreground" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 z-10">
                  {post.carousel_urls.map((_, i) => (
                    <div key={i} className={`h-1.5 rounded-full transition-all ${i === carouselIndex ? "w-4 bg-primary" : "w-1.5 bg-background/60"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <img src={post.media_url} alt={post.artist} className="h-full w-full object-cover" />
        )}

        {/* Top gradient for user tag */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-background/70 to-transparent pointer-events-none" />
        {/* Bottom gradient for comments */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background/80 via-background/40 to-transparent pointer-events-none" />

        {/* User tag — top left */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-3 z-10">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              if (userId) navigate(`/profile/${userId}`);
            }}
          >
            <Avatar className="h-8 w-8 ring-2 ring-primary/40 shadow-lg">
              {avatarUrl && <AvatarImage src={optimizeStorageUrl(avatarUrl, 64)} alt={displayName || "User avatar"} />}
              <AvatarFallback className="text-[10px] font-display bg-secondary text-foreground">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-display font-bold text-foreground text-xs drop-shadow-md truncate">
                @{displayName || "fan"}
              </p>
              <p className="text-[10px] text-foreground/70 drop-shadow-sm truncate">
                {post.artist}{post.venue ? ` · ${post.venue.split(",")[0].trim()}` : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Right side — counts removed, moved to bottom bar */}


        {/* Double-tap like animation */}
        <AnimatePresence>
          {isLikeAnimating && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1.2, opacity: 1 }}
              exit={{ scale: 1.5, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            >
              <Heart className="h-16 w-16 fill-primary text-primary drop-shadow-lg" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Play button on hover */}
        {post.media_type === "video" && !isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="flex h-14 w-14 items-center justify-center rounded-full glass text-primary"
              whileHover={{ scale: 1.15 }}
            >
              <Play className="h-6 w-6 ml-0.5" />
            </motion.div>
          </div>
        )}

        {/* Live comments — Instagram story style at bottom */}
        {!isFallback && isPlaying && liveComments.length > 0 && (
          <div className="absolute bottom-14 left-0 right-12 px-3 z-10 space-y-1.5 pointer-events-none">
            <AnimatePresence mode="popLayout">
              {liveComments.slice(-3).map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20, x: -10 }}
                  animate={{ opacity: 1 - i * 0.15, y: 0, x: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="flex items-start gap-1.5"
                >
                  <div className="rounded-full bg-background/30 backdrop-blur-sm px-2.5 py-1 max-w-[85%] truncate">
                    <span className="text-[10px] font-display font-bold text-foreground drop-shadow-sm">
                      {c.display_name}
                    </span>{" "}
                    <span className="text-[10px] text-foreground/80 drop-shadow-sm">
                      {c.content}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Comment input — bottom overlay */}
        <AnimatePresence>
          {showCommentInput && !isFallback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-0 left-0 right-0 p-2.5 z-20"
            >
              <div className="flex gap-1.5 items-center rounded-full bg-card/90 backdrop-blur-xl border border-border/50 px-3 py-1 shadow-xl">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendComment()}
                  placeholder="Add a comment..."
                  className="h-8 text-xs bg-transparent border-0 focus-visible:ring-0 px-0 placeholder:text-muted-foreground/70"
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-primary hover:text-primary shrink-0"
                  onClick={handleSendComment}
                  disabled={!newComment.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom bar */}
      <div className="px-3 py-3">
          <div className="flex items-center gap-4 mb-1.5">
            <button onClick={handleLikeClick} className="flex items-center gap-1.5 group/like">
              <Heart className={`h-5 w-5 transition-all duration-300 ${liked ? "fill-primary text-primary" : "text-muted-foreground group-hover/like:text-primary"}`} />
              <span className="text-xs font-display font-semibold text-foreground">{likeCount.toLocaleString()}</span>
            </button>
            <button onClick={onOpenDetail} className="flex items-center gap-1.5 group/comment">
              <MessageCircle className="h-5 w-5 text-muted-foreground group-hover/comment:text-primary transition-colors" />
              <span className="text-xs font-display font-semibold text-foreground">{commentCount}</span>
            </button>
          </div>
        <p className="text-[11px] text-muted-foreground line-clamp-1">
          <span className="font-semibold text-foreground mr-1">@{displayName || post.artist || "fan"}</span>
          {post.artist && post.venue ? `${post.artist} · ${post.venue.split(",")[0].trim()}` : post.description || ""}
        </p>
      </div>
    </motion.div>
  );
};

const PopularPosts = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [hoverSoundEnabled, setHoverSoundEnabled] = useState(true);
  
  const { user } = useAuth();

  const fetchPosts = useCallback(async () => {
    const { data: postsData } = await (supabase as any)
      .from("posts")
      .select("*")
      .eq("visibility", "public")
      .order("created_at", { ascending: false })
      .limit(12);

    if (!postsData || postsData.length === 0) {
      setLoaded(true);
      return;
    }

    const postIds = postsData.map((p: any) => p.id);
    const userIds = [...new Set(postsData.map((p: any) => p.user_id))] as string[];

    const [likesRes, commentsRes, profilesRes, myLikesRes] = await Promise.all([
      supabase.from("post_likes").select("post_id").in("post_id", postIds),
      supabase.from("post_comments").select("post_id").in("post_id", postIds),
      supabase.from("profiles").select("user_id, display_name, avatar_url").in("user_id", userIds),
      user
        ? supabase.from("post_likes").select("post_id").eq("user_id", user.id).in("post_id", postIds)
        : Promise.resolve({ data: [] }),
    ]);

    const myLikes = new Set(myLikesRes.data?.map(l => l.post_id) ?? []);
    const likeCounts = new Map<string, number>();
    likesRes.data?.forEach(l => likeCounts.set(l.post_id, (likeCounts.get(l.post_id) || 0) + 1));
    const commentCounts = new Map<string, number>();
    commentsRes.data?.forEach(c => commentCounts.set(c.post_id, (commentCounts.get(c.post_id) || 0) + 1));
    const profileMap = new Map(profilesRes.data?.map(p => [p.user_id, p]) ?? []);

    const enriched: Post[] = postsData.map(p => ({
      ...p,
      carousel_urls: Array.isArray(p.carousel_urls) ? (p.carousel_urls as string[]) : [],
      like_count: likeCounts.get(p.id) || 0,
      comment_count: commentCounts.get(p.id) || 0,
      liked_by_me: myLikes.has(p.id),
      display_name: profileMap.get(p.user_id)?.display_name || undefined,
      avatar_url: profileMap.get(p.user_id)?.avatar_url || undefined,
    }));

    enriched.sort((a, b) => {
      const scoreA = a.like_count * 3 + a.comment_count;
      const scoreB = b.like_count * 3 + b.comment_count;
      if (scoreB !== scoreA) return scoreB - scoreA;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    setPosts(enriched);
    setLoaded(true);
  }, [user]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleLike = async (postId: string): Promise<boolean> => {
    if (!user) return false;
    await supabase.from("post_likes").insert({ post_id: postId, user_id: user.id });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, liked_by_me: true, like_count: p.like_count + 1 } : p));
    // Check if points were already awarded for this like before (re-like scenario)
    const { data: existing } = await supabase
      .from("points_ledger")
      .select("id")
      .eq("user_id", user.id)
      .eq("source_table", "post_likes")
      .eq("source_row_id", postId)
      .eq("event_type", "post_like_given")
      .limit(1);
    return !existing || existing.length === 0;
  };

  const handleUnlike = async (postId: string) => {
    if (!user) return;
    await supabase.from("post_likes").delete().eq("post_id", postId).eq("user_id", user.id);
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, liked_by_me: false, like_count: Math.max(0, p.like_count - 1) } : p));
  };

  const selectedPost = posts.find((p) => p.id === selectedPostId) || null;

  if (loaded && posts.length === 0) return null;

  return (
    <section className="py-14 sm:py-20 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-10 flex items-end justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1 w-8 rounded-full gradient-hero" />
              <span className="text-xs font-display font-bold text-primary uppercase tracking-widest">Feed</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Trending Now</h2>
            <div className="mt-1.5 flex items-center gap-3">
              <p className="text-sm text-muted-foreground">The hottest concert clips from the community</p>
              <button
                onClick={() => {
                  const next = !hoverSoundEnabled;
                  setHoverSoundEnabled(next);
                  localStorage.setItem("encore_hover_sound", String(next));
                }}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors border ${
                  hoverSoundEnabled
                    ? "bg-primary/10 text-primary border-primary/30"
                    : "bg-muted text-muted-foreground border-border/50"
                }`}
                title={hoverSoundEnabled ? "Mute hover audio" : "Unmute hover audio"}
              >
                {hoverSoundEnabled ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                {!isMobile && (hoverSoundEnabled ? "Sound On" : "Sound Off")}
              </button>
            </div>
          </div>
          <motion.button
            whileHover={{ x: 4 }}
            className="text-sm font-display font-medium text-primary hover:underline underline-offset-4"
            onClick={() => {
              if (posts.length > 0) setSelectedPostId(posts[0].id);
            }}
          >
            See all →
          </motion.button>
        </motion.div>

        {isMobile ? (
          /* Mobile: single-column grid with smaller cards */
          <div className="flex flex-col items-center gap-3 px-4">
            {posts.map((post, index) => (
              <VideoCard
                key={post.id}
                post={{ id: post.id, artist: post.artist, media_url: post.media_url, media_type: post.media_type, description: post.description, venue: post.venue, poster: post.thumbnail_url, carousel_urls: post.carousel_urls }}
                index={index}
                onLike={() => handleLike(post.id)}
                onUnlike={() => handleUnlike(post.id)}
                liked={post.liked_by_me}
                likeCount={post.like_count}
                commentCount={post.comment_count}
                displayName={post.display_name}
                avatarUrl={post.avatar_url}
                userId={post.user_id}
                onOpenDetail={() => setSelectedPostId(post.id)}
                hoverSoundEnabled={hoverSoundEnabled}
              />
            ))}
          </div>
        ) : (
          /* Desktop: row-based grid, 3 per row, fill first row then second */
          (() => {
            const COLS = 3;
            const MAX_VISIBLE = COLS * 2; // 2 rows
            const visiblePosts = posts.slice(0, MAX_VISIBLE - (posts.length > MAX_VISIBLE ? 1 : 0));
            const hasMore = posts.length > MAX_VISIBLE - 1;

            return (
              <div className="grid grid-cols-3 gap-2.5 sm:gap-3 lg:gap-4 w-full" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", maxWidth: "1100px" }}>
                {visiblePosts.map((post, index) => (
                  <VideoCard
                    key={post.id}
                    post={{ id: post.id, artist: post.artist, media_url: post.media_url, media_type: post.media_type, description: post.description, venue: post.venue, poster: post.thumbnail_url, carousel_urls: post.carousel_urls }}
                    index={index}
                    onLike={() => handleLike(post.id)}
                    onUnlike={() => handleUnlike(post.id)}
                    liked={post.liked_by_me}
                    likeCount={post.like_count}
                    commentCount={post.comment_count}
                    displayName={post.display_name}
                    avatarUrl={post.avatar_url}
                    userId={post.user_id}
                    onOpenDetail={() => setSelectedPostId(post.id)}
                    hoverSoundEnabled={hoverSoundEnabled}
                  />
                ))}
                {hasMore && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-center rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm cursor-pointer group hover:border-primary/30 transition-colors min-h-[200px]"
                    onClick={() => navigate("/artists")}
                  >
                    <div className="text-center px-4">
                      <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <ChevronRight className="h-6 w-6 text-primary" />
                      </div>
                      <p className="font-display font-bold text-sm text-foreground">See More</p>
                      <p className="text-xs text-muted-foreground mt-1">Explore all clips</p>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })()
        )}

        {/* Video Detail Modal with navigation */}
        {selectedPost && (() => {
          const currentIndex = posts.findIndex(p => p.id === selectedPostId);
          const hasPrev = currentIndex > 0;
          const hasNext = currentIndex < posts.length - 1;
          return (
            <VideoDetailModal
              open={!!selectedPostId}
              onClose={() => setSelectedPostId(null)}
              post={{ id: selectedPost.id, artist: selectedPost.artist, media_url: selectedPost.media_url, media_type: selectedPost.media_type, description: selectedPost.description, venue: selectedPost.venue }}
              liked={selectedPost.liked_by_me}
              likeCount={selectedPost.like_count}
              commentCount={selectedPost.comment_count}
              displayName={selectedPost.display_name}
              avatarUrl={selectedPost.avatar_url}
              userId={selectedPost.user_id}
              onLike={() => handleLike(selectedPost.id)}
              onUnlike={() => handleUnlike(selectedPost.id)}
              onNext={hasNext ? () => setSelectedPostId(posts[currentIndex + 1].id) : undefined}
              onPrev={hasPrev ? () => setSelectedPostId(posts[currentIndex - 1].id) : undefined}
              hasNext={hasNext}
              hasPrev={hasPrev}
            />
          );
        })()}
      </div>
    </section>
  );
};

export default PopularPosts;
